import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { getAllReports } from "../../../redux/service/action";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import FullScreenLoader from "../../Layouts/FullScreenLoader/FullScreenLoader";
import { formatDateTime } from "../../Common/Utils/dateUtils";
import { formatPurchaseSourceLabel, getMembershipTermLabel } from "../../Common/Utils/adminViewUtils";
import TestingAccountFilter from "../../Common/TestingAccountFilter";
import "../../Common/Styles/AdminEnhancements.css";

const INITIAL_PARAMS = {
  type: "payment_logs_report",
  page: 1,
  per_page: 10,
  search_key: "",
  activity_type: "",
  status: "",
  purchase_source: "",
  payment_type: "",
  start_date: "",
  end_date: "",
  testing_account_filter: "hide",
};

const INITIAL_UNMATCHED_PARAMS = { page: 1, per_page: 5 };

const ACTIVITY_OPTIONS = [
  { value: "", label: "All Recent Activity" },
  { value: "cancelled", label: "Membership Cancellations" },
  { value: "failed", label: "Failures / Blocked" },
  { value: "renewal", label: "Renewal Activity" },
  { value: "completed", label: "Completed Initial Payments" },
  { value: "pending", label: "Pending Initial Payments" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Initial Payment Statuses" },
  { value: "1", label: "Completed" },
  { value: "0", label: "Pending" },
  { value: "2", label: "Failed" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "website", label: "Website" },
  { value: "shopify", label: "Shopify" },
  { value: "wordpress", label: "WordPress" },
  { value: "unknown", label: "Unknown" },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: "", label: "All Payment Types" },
  { value: "1", label: "Membership" },
  { value: "2", label: "Add-on" },
];

const getStatusClass = (status) => {
  if (Number(status) === 1) return "success";
  if (Number(status) === 2) return "danger";
  return "warning";
};

const getActivityClass = (kind) => {
  if (kind === "failed") return "danger";
  if (kind === "cancelled" || kind === "inactive") return "warning";
  if (kind === "completed") return "success";
  if (kind === "renewal" || kind === "webhook") return "info";
  if (kind === "pending") return "warning";
  return "neutral";
};

const getDirectionClass = (direction) => {
  if (direction === "outbound") return "info";
  if (direction === "inbound") return "success";
  return "warning";
};

const getRecurringStatusClass = (status) => {
  if (status === "active" || status === "completed") return "success";
  if (status === "failed") return "danger";
  if (status === "cancelled" || status === "pending") return "warning";
  return "info";
};

const formatAmount = (value) => {
  const parsed = Number(value ?? 0);
  if (Number.isNaN(parsed)) return "--";
  return parsed.toFixed(2);
};

const formatPaymentAmount = (row) => {
  const currency = row?.billing_currency || (row?.source_channel === "shopify" ? "USD" : "SAR");
  return `${currency} ${formatAmount(row?.amount)}`;
};

const normalizePrettyJson = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  const text = String(value);
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch (error) {
    return text;
  }
};

const getTimelineSourceLabel = (source) => {
  if (source === "persisted") return "Recorded event timeline · newest first";
  if (source === "legacy_fallback" || source === "frontend_legacy_fallback") return "Legacy payment records · newest first";
  return "No recorded timeline";
};

const sortTimelineNewestFirst = (events) =>
  [...(Array.isArray(events) ? events : [])].sort((left, right) => {
    const leftTime = String(left?.created_on || "").replace(" ", "T");
    const rightTime = String(right?.created_on || "").replace(" ", "T");
    if (leftTime !== rightTime) return rightTime.localeCompare(leftTime);
    return Number(right?.event_id || 0) - Number(left?.event_id || 0);
  });

const buildLegacyTimelineEventsFromRow = (row) => {
  if (!row) return [];
  const events = [];

  if (row?.first_api_post) {
    events.push({
      event_id: null,
      direction: "outbound",
      direction_label: "Outbound",
      event_type: "legacy_first_api_request",
      event_type_label: "Legacy Outbound Request",
      status_label: "legacy",
      created_on: row?.created_on || null,
      endpoint: "",
      payload: row.first_api_post,
      payload_preview: row.first_api_post_preview || "--",
      related_invoice_id: row?.invoice_id || "--",
      related_recurring_id: "--",
      action_title: "Payment Initiated",
      action_hint: "Built from the original transaction record",
    });
  }

  if (row?.first_api_res) {
    events.push({
      event_id: null,
      direction: "inbound",
      direction_label: "Inbound",
      event_type: "legacy_first_api_response",
      event_type_label: "Legacy Gateway Reply",
      status_label: "legacy",
      created_on: row?.updated_on || row?.created_on || null,
      endpoint: "",
      payload: row.first_api_res,
      payload_preview: row.first_api_res_preview || "--",
      related_invoice_id: row?.invoice_id || "--",
      related_recurring_id: "--",
      action_title: row?.action_title || "Gateway Reply",
      action_hint: "Built from the original transaction record",
    });
  }

  const inboundPayload = row?.second_api_res || row?.second_api_post || row?.payment_response_json || "";
  if (inboundPayload) {
    events.push({
      event_id: null,
      direction: "inbound",
      direction_label: "Inbound",
      event_type: "legacy_inbound_callback",
      event_type_label: "Legacy Callback/Webhook",
      status_label: "legacy",
      created_on: row?.updated_on || row?.created_on || null,
      endpoint: "",
      payload: inboundPayload,
      payload_preview:
        row?.inbound_preview ||
        row?.second_api_res_preview ||
        row?.second_api_post_preview ||
        row?.payment_response_preview ||
        "--",
      related_invoice_id: row?.invoice_id || "--",
      related_recurring_id: "--",
      action_title: row?.action_title || "Webhook/callback received",
      action_hint: "Built from the original transaction record",
    });
  }

  return events;
};

const TechnicalPayload = ({ label, value, preview }) => {
  if (!value) return null;
  return (
    <details className="payment-technical-details">
      <summary>{label}</summary>
      {preview ? <div className="payment-payload-preview">{preview}</div> : null}
      <pre>{normalizePrettyJson(value)}</pre>
    </details>
  );
};

const PaymentLogsReport = () => {
  const navigate = useNavigate();
  const [params, setParams] = useState(INITIAL_PARAMS);
  const [unmatchedParams, setUnmatchedParams] = useState(INITIAL_UNMATCHED_PARAMS);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [timelineDetails, setTimelineDetails] = useState({});
  const [failureDetails, setFailureDetails] = useState({});
  const [reportsData, setReportsData] = useState({
    payment_logs_report: [],
    pagination_meta: null,
    event_logs_degraded: false,
    event_logs_degraded_reason: "",
  });
  const [unmatchedData, setUnmatchedData] = useState({
    unmatched_webhook_report: [],
    pagination_meta: null,
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setParams((prev) => ({ ...prev, search_key: searchText, page: 1 }));
      setUnmatchedParams((prev) => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  useEffect(() => {
    let active = true;
    const fetchReport = async () => {
      setLoading(true);
      try {
        const response = await getAllReports(params);
        if (!active) return;
        if (response?.data?.status) {
          const payload = response?.data?.response || {};
          setReportsData({
            payment_logs_report: payload?.payment_logs_report || [],
            pagination_meta: payload?.pagination_meta || null,
            event_logs_degraded: Boolean(payload?.event_logs_degraded),
            event_logs_degraded_reason: payload?.event_logs_degraded_reason || "",
          });
        } else {
          setReportsData({
            payment_logs_report: [],
            pagination_meta: null,
            event_logs_degraded: false,
            event_logs_degraded_reason: "",
          });
        }
      } catch (error) {
        if (active) {
          console.error(error);
          setReportsData({
            payment_logs_report: [],
            pagination_meta: null,
            event_logs_degraded: false,
            event_logs_degraded_reason: "",
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchReport();
    return () => {
      active = false;
    };
  }, [params]);

  useEffect(() => {
    let active = true;
    const fetchUnmatched = async () => {
      setUnmatchedLoading(true);
      try {
        const response = await getAllReports({
          type: "unmatched_webhook_report",
          page: unmatchedParams.page,
          per_page: unmatchedParams.per_page,
          search_key: params.search_key,
          start_date: params.start_date,
          end_date: params.end_date,
        });
        if (!active) return;
        if (response?.data?.status) {
          const payload = response?.data?.response || {};
          setUnmatchedData({
            unmatched_webhook_report: payload?.unmatched_webhook_report || [],
            pagination_meta: payload?.pagination_meta || null,
          });
        } else {
          setUnmatchedData({ unmatched_webhook_report: [], pagination_meta: null });
        }
      } catch (error) {
        if (active) {
          console.error(error);
          setUnmatchedData({ unmatched_webhook_report: [], pagination_meta: null });
        }
      } finally {
        if (active) setUnmatchedLoading(false);
      }
    };
    fetchUnmatched();
    return () => {
      active = false;
    };
  }, [unmatchedParams, params.search_key, params.start_date, params.end_date]);

  const rows = useMemo(
    () => reportsData?.payment_logs_report || [],
    [reportsData?.payment_logs_report]
  );
  const unmatchedRows = useMemo(
    () => unmatchedData?.unmatched_webhook_report || [],
    [unmatchedData?.unmatched_webhook_report]
  );

  useEffect(() => {
    let active = true;
    const candidates = rows.filter(
      (row) =>
        row?.transaction_id &&
        row?.latest_renewal_status_code === "failed" &&
        row?.latest_renewal_provider_lookup_required
    );

    if (candidates.length === 0) return () => {
      active = false;
    };

    setFailureDetails((prev) => {
      const next = { ...prev };
      candidates.forEach((row) => {
        next[row.transaction_id] = { loading: true, error: "" };
      });
      return next;
    });

    Promise.all(
      candidates.map(async (row) => {
        try {
          const response = await getAllReports({
            type: "payment_failure_detail",
            transaction_id: row.transaction_id,
          });
          return {
            transactionId: row.transaction_id,
            detail: response?.data?.status
              ? response?.data?.response?.payment_failure_detail || {}
              : {},
            error: "",
          };
        } catch (error) {
          console.error(error);
          return {
            transactionId: row.transaction_id,
            detail: {},
            error: "Provider reason is temporarily unavailable.",
          };
        }
      })
    ).then((results) => {
      if (!active) return;
      setFailureDetails((prev) => {
        const next = { ...prev };
        results.forEach(({ transactionId, detail, error }) => {
          next[transactionId] = { loading: false, error, ...detail };
        });
        return next;
      });
    });

    return () => {
      active = false;
    };
  }, [rows]);

  const summary = useMemo(() => {
    const cancellations = rows.filter((row) => row?.activity_kind === "cancelled").length;
    const failures = rows.filter((row) => row?.activity_kind === "failed").length;
    const completed = rows.filter((row) => row?.activity_kind === "completed").length;
    const pending = rows.filter((row) => row?.activity_kind === "pending").length;

    return [
      {
        label: "Activities Found",
        value: reportsData?.pagination_meta?.total ?? rows.length,
        subtext: "Newest activity appears first",
      },
      { label: "Cancellations Shown", value: cancellations, subtext: "On this page" },
      { label: "Failures Shown", value: failures, subtext: "On this page" },
      { label: "Successful Latest Activity", value: completed, subtext: "On this page" },
      { label: "Pending Latest Activity", value: pending, subtext: "On this page" },
      {
        label: "Unmatched Webhooks",
        value: unmatchedData?.pagination_meta?.total || unmatchedRows.length,
        subtext: "Received without a transaction match",
      },
    ];
  }, [rows, reportsData, unmatchedData, unmatchedRows]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setParams((prev) => ({ ...prev, [name]: value, page: 1 }));
    setExpandedRows({});
    setTimelineDetails({});
    setUnmatchedParams((prev) => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    setSearchText("");
    setParams(INITIAL_PARAMS);
    setUnmatchedParams(INITIAL_UNMATCHED_PARAMS);
    setExpandedRows({});
    setTimelineDetails({});
  };

  const loadTimeline = async ({ rowKey, transactionId, eventGroupKey, row }) => {
    setTimelineDetails((prev) => ({
      ...prev,
      [rowKey]: { loading: true, context: {}, events: [], error: "" },
    }));

    try {
      const payload = { type: "payment_log_timeline" };
      if (transactionId) payload.transaction_id = transactionId;
      if (!transactionId && eventGroupKey) payload.event_group_key = eventGroupKey;

      const response = await getAllReports(payload);
      let context = {};
      let events = [];
      let eventLogsDegraded = false;
      let eventLogsDegradedReason = "";

      if (response?.data?.status) {
        const timelinePayload = response?.data?.response || {};
        context = timelinePayload?.context && typeof timelinePayload.context === "object" ? timelinePayload.context : {};
        events = Array.isArray(timelinePayload?.timeline_events) ? timelinePayload.timeline_events : [];
        eventLogsDegraded = Boolean(timelinePayload?.event_logs_degraded || context?.event_logs_degraded);
        eventLogsDegradedReason =
          timelinePayload?.event_logs_degraded_reason || context?.event_logs_degraded_reason || "";
      }

      if (events.length === 0 && row) {
        const fallbackEvents = buildLegacyTimelineEventsFromRow(row);
        if (fallbackEvents.length > 0) {
          events = fallbackEvents;
          context = {
            ...context,
            transaction_id: context?.transaction_id || row?.transaction_id,
            event_group_key:
              context?.event_group_key || row?.event_group_key || `txn:${row?.transaction_id || "--"}`,
            timeline_available: true,
            timeline_source: context?.timeline_source || "frontend_legacy_fallback",
            timeline_note:
              context?.timeline_note ||
              "Showing the original request/response records because this older transaction has no persisted event timeline.",
          };
        }
      }

      events = sortTimelineNewestFirst(events);

      setTimelineDetails((prev) => ({
        ...prev,
        [rowKey]: {
          loading: false,
          context: {
            ...context,
            event_logs_degraded: eventLogsDegraded,
            event_logs_degraded_reason: eventLogsDegradedReason,
          },
          events,
          error: "",
        },
      }));
    } catch (error) {
      console.error(error);
      setTimelineDetails((prev) => ({
        ...prev,
        [rowKey]: {
          loading: false,
          context: {},
          events: [],
          error: "Unable to load the detailed activity timeline.",
        },
      }));
    }
  };

  const toggleExpanded = ({ rowKey, transactionId, eventGroupKey, row }) => {
    const willOpen = !expandedRows[rowKey];
    setExpandedRows((prev) => ({ ...prev, [rowKey]: willOpen }));
    if (willOpen && !timelineDetails[rowKey]) {
      loadTimeline({ rowKey, transactionId, eventGroupKey, row });
    }
  };

  const renderTimeline = (rowKey) => {
    const details = timelineDetails[rowKey];
    if (!details || details.loading) {
      return <div className="payment-inline-loading">Loading full activity details...</div>;
    }
    if (details.error) {
      return <div className="admin-highlight-box">{details.error}</div>;
    }

    const timelineEmptyMessage = details?.context?.event_logs_degraded
      ? "The recorded timeline is temporarily unavailable. Original transaction records are shown when available."
      : "No detailed timeline exists for this older activity.";

    return (
      <div className="payment-timeline-section">
        <div className="payment-timeline-heading">
          <div>
            <h6>Complete Activity Timeline</h6>
            <p>{getTimelineSourceLabel(details?.context?.timeline_source)}</p>
          </div>
          {details?.context?.flow_stage ? (
            <span className="admin-chip admin-chip--info">{details.context.flow_stage}</span>
          ) : null}
        </div>
        {details?.context?.timeline_note ? (
          <div className="admin-highlight-box mb-3">{details.context.timeline_note}</div>
        ) : null}
        {details?.events?.length > 0 ? (
          <div className="payment-timeline">
            {details.events.map((eventItem, index) => (
              <div className="payment-timeline-item" key={eventItem?.event_id || `${eventItem?.event_type}-${index}`}>
                <div className="payment-timeline-marker" aria-hidden="true" />
                <div className="payment-timeline-content">
                  <div className="payment-timeline-topline">
                    <strong>{eventItem?.action_title || eventItem?.event_type_label || "Payment activity"}</strong>
                    <span>{formatDateTime(eventItem?.created_on)}</span>
                  </div>
                  <div className="payment-chip-row">
                    <span className={`admin-chip admin-chip--${getDirectionClass(eventItem?.direction)}`}>
                      {eventItem?.direction_label || eventItem?.direction || "Internal"}
                    </span>
                    <span className="admin-chip">{eventItem?.event_type_label || eventItem?.event_type || "Event"}</span>
                    {eventItem?.status_label ? <span className="admin-chip">{eventItem.status_label}</span> : null}
                  </div>
                  {eventItem?.action_hint ? <p className="payment-timeline-note">{eventItem.action_hint}</p> : null}
                  <div className="payment-timeline-note">
                    Invoice: {eventItem?.related_invoice_id || "--"} · Recurring: {eventItem?.related_recurring_id || "--"}
                  </div>
                  <TechnicalPayload
                    label="Show technical payload"
                    value={eventItem?.payload}
                    preview={eventItem?.payload_preview}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="admin-empty-state">{timelineEmptyMessage}</div>
        )}
      </div>
    );
  };

  return (
    <Card className="py-3 payment-activity-page">
      <div className="px-3">
        <div className="admin-toolbar payment-activity-toolbar">
          <div>
            <h4 className="mb-1">Payment Activity</h4>
            <div className="admin-toolbar-note">
              Newest activity first. Filter cancellations, failed payments, renewals, or pending payments and open one
              clear detail section only when you need the technical evidence.
            </div>
          </div>
        </div>

        <div className="row payment-activity-filters mb-2">
          <div className="col-lg-4 col-md-6 form-group">
            <label>Search Customer or Reference</label>
            <input
              type="text"
              className="form-control"
              placeholder="Company, name, email, user, transaction, or invoice ID"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <div className="col-lg-2 col-md-3 form-group">
            <label>Activity</label>
            <select className="form-control" name="activity_type" value={params.activity_type} onChange={handleFilterChange}>
              {ACTIVITY_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-lg-2 col-md-3 form-group">
            <label>Initial Payment Status</label>
            <select className="form-control" name="status" value={params.status} onChange={handleFilterChange}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-lg-2 col-md-3 form-group">
            <label>Source</label>
            <select className="form-control" name="purchase_source" value={params.purchase_source} onChange={handleFilterChange}>
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-lg-2 col-md-3 form-group">
            <label>Payment Type</label>
            <select className="form-control" name="payment_type" value={params.payment_type} onChange={handleFilterChange}>
              {PAYMENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <TestingAccountFilter
            className="col-lg-2 col-md-3 form-group"
            value={params.testing_account_filter}
            onChange={handleFilterChange}
          />
          <div className="col-lg-2 col-md-3 form-group">
            <label>Activity From</label>
            <input type="date" className="form-control" name="start_date" value={params.start_date} onChange={handleFilterChange} />
          </div>
          <div className="col-lg-2 col-md-3 form-group">
            <label>Activity To</label>
            <input
              type="date"
              className="form-control"
              name="end_date"
              value={params.end_date}
              min={params.start_date || undefined}
              onChange={handleFilterChange}
            />
          </div>
          <div className="col-lg-2 col-md-3 form-group d-flex align-items-end">
            <button type="button" className="submit-btn w-100" onClick={resetFilters}>
              Reset Filters
            </button>
          </div>
        </div>

        <div className="admin-summary-grid payment-activity-summary-grid">
          {summary.map((item) => (
            <div className="admin-summary-card" key={item.label}>
              <div className="admin-summary-label">{item.label}</div>
              <div className="admin-summary-value">{item.value}</div>
              <div className="admin-summary-subtext">{item.subtext}</div>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <FullScreenLoader />
      ) : (
        <CardBody className="px-3">
          {reportsData?.event_logs_degraded ? (
            <div className="admin-highlight-box mb-3">
              The detailed event timeline is temporarily degraded. The payment activity list and available original
              transaction records remain visible.
            </div>
          ) : null}

          <div className="payment-activity-list">
            {rows.length > 0 ? (
              rows.map((row, index) => {
                const rowKey = row?.transaction_id ? `txn-${row.transaction_id}` : `row-${index}`;
                const isExpanded = Boolean(expandedRows[rowKey]);
                const activityClass = getActivityClass(row?.activity_kind);
                const customerTitle = row?.company_name || row?.customer_name || "Unknown customer";
                const customerSubtitle = row?.company_name && row?.customer_name ? row.customer_name : row?.email_id || "";
                const providerFailureDetail = failureDetails[row?.transaction_id] || {};
                const latestRenewalStatusCode =
                  providerFailureDetail?.latest_renewal_status_code || row?.latest_renewal_status_code || "";
                const latestRenewalStatusLabel =
                  providerFailureDetail?.latest_renewal_status_label || row?.latest_renewal_status_label || "";
                const latestRecurringStatusCode =
                  providerFailureDetail?.latest_recurring_status_code || row?.latest_recurring_status_code || "";
                const latestRecurringStatusLabel =
                  providerFailureDetail?.latest_recurring_status_label || row?.latest_recurring_status_label || "";
                const latestRenewalReason =
                  providerFailureDetail?.failure_reason || row?.latest_renewal_reason || "";
                const providerReasonLoading = Boolean(providerFailureDetail?.loading);
                const cardTitle =
                  latestRenewalStatusCode === "failed"
                    ? "Renewal Payment Failed"
                    : latestRenewalStatusCode === "cancelled"
                    ? "Recurring Membership Cancelled"
                    : row?.action_title || "Payment activity";

                return (
                  <article className={`payment-activity-card payment-activity-card--${activityClass}`} key={rowKey}>
                    <div className="payment-activity-card-header">
                      <div>
                        <div className="payment-activity-time">{formatDateTime(row?.activity_at || row?.updated_on || row?.created_on)}</div>
                        <h5>{cardTitle}</h5>
                        <div className="payment-activity-customer">
                          {customerTitle}
                          {customerSubtitle ? <span>{customerSubtitle}</span> : null}
                          <span>User ID: {row?.user_id || "--"}</span>
                        </div>
                      </div>
                      <div className="payment-activity-amount-block">
                        <strong>{formatPaymentAmount(row)}</strong>
                        <span>{row?.payment_type === 2 ? "Add-on" : row?.membership_title || "Membership"}</span>
                      </div>
                    </div>

                    <div className="payment-chip-row">
                      <span className={`admin-chip admin-chip--${getStatusClass(row?.status)}`}>
                        Initial Payment: {row?.initial_payment_status_label || row?.status_label || "Unknown"}
                      </span>
                      {latestRenewalStatusLabel ? (
                        <span className={`admin-chip admin-chip--${getActivityClass(latestRenewalStatusCode)}`}>
                          Latest Renewal: {latestRenewalStatusLabel}
                        </span>
                      ) : null}
                      {latestRecurringStatusLabel ? (
                        <span className={`admin-chip admin-chip--${getRecurringStatusClass(latestRecurringStatusCode)}`}>
                          Recurring: {latestRecurringStatusLabel}
                        </span>
                      ) : null}
                      <span className="admin-chip">{formatPurchaseSourceLabel(row?.purchase_source_label) || "Unknown source"}</span>
                    </div>

                    <div className="payment-activity-explanation">
                      <strong>{row?.activity_summary || row?.reaction_label || "Payment activity recorded."}</strong>
                      {providerReasonLoading ? <span>Reason: Checking the payment provider...</span> : null}
                      {!providerReasonLoading && latestRenewalStatusCode === "failed" ? (
                        <span>
                          Reason: {latestRenewalReason || providerFailureDetail?.error || "The provider did not supply an exact decline reason."}
                        </span>
                      ) : null}
                      {providerFailureDetail?.provider_invoice_status_label ? (
                        <span>Provider invoice: {providerFailureDetail.provider_invoice_status_label}</span>
                      ) : null}
                      {row?.activity_origin_label ? <span>Origin: {row.activity_origin_label}</span> : null}
                      {row?.activity_provider_label ? <span>Provider: {row.activity_provider_label}</span> : null}
                      {row?.activity_reason && latestRenewalStatusCode !== "failed" ? <span>Reason: {row.activity_reason}</span> : null}
                    </div>

                    <div className="payment-activity-meta-grid">
                      <div className="payment-activity-meta-item">
                        <span>Membership</span>
                        <strong>{row?.membership_title || "--"}</strong>
                        <small>{getMembershipTermLabel(row) || "Term unavailable"}</small>
                      </div>
                      <div className="payment-activity-meta-item">
                        <span>Latest Renewal</span>
                        <strong>{latestRenewalStatusLabel || "No renewal recorded"}</strong>
                        <small>{row?.latest_renewal_at ? formatDateTime(row.latest_renewal_at) : "Initial payment only"}</small>
                      </div>
                      <div className="payment-activity-meta-item">
                        <span>Recurring Agreement</span>
                        <strong>{latestRecurringStatusLabel || "No recurring update"}</strong>
                        <small>{row?.latest_membership_end_date ? `Current access period ends ${row.latest_membership_end_date}` : "No access end date"}</small>
                      </div>
                      <div className="payment-activity-meta-item">
                        <span>Webhook Evidence</span>
                        <strong>{row?.webhook_received ? "Received" : "Not recorded"}</strong>
                        <small>{row?.timeline_available ? `${row?.event_count || 0} events · Txn ${row?.transaction_id || "--"}` : "Legacy/no timeline"}</small>
                      </div>
                    </div>

                    <div className="payment-activity-actions">
                      <button
                        type="button"
                        className="border-button"
                        onClick={() => toggleExpanded({ rowKey, transactionId: row?.transaction_id, row })}
                      >
                        <span>{isExpanded ? "Hide full details" : "View full details"}</span>
                      </button>
                      {row?.user_id ? (
                        <button type="button" className="border-button" onClick={() => navigate(`/user/update/${row.user_id}`)}>
                          <span>Open customer</span>
                        </button>
                      ) : null}
                    </div>

                    {isExpanded ? (
                      <div className="payment-activity-expanded">
                        <div className="payment-details-grid">
                          <div>
                            <h6>Customer</h6>
                            <p>{row?.company_name || "--"}</p>
                            <p>{row?.customer_name || "--"}</p>
                            <p>{row?.email_id || "--"}</p>
                            <p>{`${row?.country_code || ""} ${row?.mobile_number || ""}`.trim() || "--"}</p>
                          </div>
                          <div>
                            <h6>Payment Context</h6>
                            <p>Created: {formatDateTime(row?.created_on)}</p>
                            <p>Last activity: {formatDateTime(row?.activity_at || row?.updated_on)}</p>
                            <p>Initial payment: {row?.initial_payment_status_label || row?.status_label || "--"}</p>
                            <p>Latest renewal: {latestRenewalStatusLabel || "No renewal recorded"}</p>
                            <p>Recurring agreement: {latestRecurringStatusLabel || "No recurring update"}</p>
                            {latestRenewalReason ? <p>Failure reason: {latestRenewalReason}</p> : null}
                            <p>Store: {row?.store_name || "--"}</p>
                            <p>Stage: {row?.flow_stage || "--"}</p>
                          </div>
                          <div>
                            <h6>Identifiers</h6>
                            <p>Transaction: {row?.transaction_id || "--"}</p>
                            <p>Membership: {row?.membership_id || "--"}</p>
                            <p>Invoice: {row?.invoice_id || "--"}</p>
                            <p>Event group: {row?.event_group_key || "--"}</p>
                          </div>
                        </div>

                        {renderTimeline(rowKey)}

                        <div className="payment-original-records">
                          <h6>Original Gateway Records</h6>
                          <p>These technical records remain available for investigation without opening another popup.</p>
                          <TechnicalPayload label="Request sent to gateway" value={row?.first_api_post} preview={row?.outbound_preview} />
                          <TechnicalPayload label="Immediate gateway response" value={row?.first_api_res} preview={row?.gateway_reply_preview} />
                          <TechnicalPayload
                            label="Callback / webhook payload"
                            value={row?.second_api_res || row?.second_api_post || row?.payment_response_json}
                            preview={row?.inbound_preview}
                          />
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })
            ) : (
              <div className="admin-empty-state payment-activity-empty">No payment activity matches these filters.</div>
            )}
          </div>

          <CommonPagination params={params} setParams={setParams} pagination_meta={reportsData?.pagination_meta} />

          <div className="admin-section-card payment-unmatched-section mt-4">
            <div className="admin-section-title">
              <div>
                <h5 className="mb-1">Unmatched Webhooks</h5>
                <p className="admin-section-note mb-0">
                  Provider messages received without a matching transaction. Review these when a payment appears missing.
                </p>
              </div>
              <span className="admin-chip admin-chip--warning">
                {unmatchedData?.pagination_meta?.total || unmatchedRows.length} events
              </span>
            </div>

            {unmatchedLoading ? (
              <FullScreenLoader />
            ) : unmatchedRows.length > 0 ? (
              <div className="payment-unmatched-list">
                {unmatchedRows.map((row, index) => {
                  const rowKey = `unmatched-${row?.event_id || `${row?.event_group_key}-${index}`}`;
                  const isExpanded = Boolean(expandedRows[rowKey]);
                  return (
                    <article className="payment-unmatched-card" key={rowKey}>
                      <div className="payment-unmatched-card-header">
                        <div>
                          <span className="payment-activity-time">{formatDateTime(row?.created_on)}</span>
                          <h6>{row?.action_title || row?.event_type_label || "Unmatched webhook"}</h6>
                        </div>
                        <span className={`admin-chip admin-chip--${getDirectionClass(row?.direction)}`}>
                          {formatPurchaseSourceLabel(row?.source_channel || "Unknown")}
                        </span>
                      </div>
                      <p>{row?.action_hint || "This provider activity could not be linked to a transaction."}</p>
                      <div className="payment-activity-meta-grid">
                        <div className="payment-activity-meta-item">
                          <span>Event</span>
                          <strong>{row?.event_type_label || "--"}</strong>
                          <small>{row?.status_label || "unmatched"}</small>
                        </div>
                        <div className="payment-activity-meta-item">
                          <span>Group</span>
                          <strong>{row?.event_group_key || "--"}</strong>
                          <small>Event ID {row?.event_id || "--"}</small>
                        </div>
                        <div className="payment-activity-meta-item">
                          <span>Related References</span>
                          <strong>Invoice {row?.related_invoice_id || "--"}</strong>
                          <small>Recurring {row?.related_recurring_id || "--"}</small>
                        </div>
                      </div>
                      <div className="payment-activity-actions">
                        <button
                          type="button"
                          className="border-button"
                          onClick={() =>
                            toggleExpanded({
                              rowKey,
                              eventGroupKey: row?.event_group_key || `event:${row?.event_id}`,
                            })
                          }
                        >
                          <span>{isExpanded ? "Hide related activity" : "View related activity"}</span>
                        </button>
                      </div>
                      <TechnicalPayload label="Show webhook payload" value={row?.payload} preview={row?.payload_preview} />
                      {isExpanded ? <div className="payment-activity-expanded">{renderTimeline(rowKey)}</div> : null}
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="admin-empty-state">No unmatched webhooks found.</div>
            )}

            <CommonPagination
              params={unmatchedParams}
              setParams={setUnmatchedParams}
              pagination_meta={unmatchedData?.pagination_meta}
            />
          </div>
        </CardBody>
      )}
    </Card>
  );
};

export default PaymentLogsReport;
